import { useState } from "react";
import { ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function MfaVerify({ challenge, onDone }) {
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = useRecovery ? { challenge, recovery_code: recovery.trim() } : { challenge, code };
      const { data } = await api.post("/auth/login/mfa", body);
      onDone(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Verification failed");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="glass glass-cyan rounded-2xl p-7" data-testid="mfa-verify-form">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-5 h-5 text-cyan-400" />
        <h1 className="font-head font-bold text-lg">Two-factor verification</h1>
      </div>
      <p className="text-xs text-muted-foreground mb-5 font-mono">
        {useRecovery ? "Enter one of your saved recovery codes." : "Enter the 6-digit code from your authenticator app."}
      </p>

      {!useRecovery ? (
        <div className="space-y-1">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Authenticator code</Label>
          <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                 inputMode="numeric" autoFocus placeholder="123456" data-testid="mfa-code-input"
                 className="bg-slate-900/60 border-white/10 tracking-[0.4em] text-center font-mono text-lg" />
        </div>
      ) : (
        <div className="space-y-1">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Recovery code</Label>
          <Input value={recovery} onChange={(e) => setRecovery(e.target.value)} autoFocus
                 placeholder="xxxx-xxxx-xxxx" data-testid="mfa-recovery-input"
                 className="bg-slate-900/60 border-white/10 font-mono" />
        </div>
      )}

      <Button type="submit" disabled={busy || (!useRecovery && code.length !== 6) || (useRecovery && !recovery.trim())}
              data-testid="mfa-verify-submit"
              className="w-full mt-5 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
        {busy ? "Verifying…" : "Verify & sign in"}
      </Button>

      <button type="button" onClick={() => { setUseRecovery(!useRecovery); setCode(""); setRecovery(""); }}
              data-testid="mfa-toggle-recovery"
              className="mt-3 w-full text-xs font-mono text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5">
        <KeyRound className="w-3.5 h-3.5" /> {useRecovery ? "Use authenticator code instead" : "Use a recovery code instead"}
      </button>
    </form>
  );
}
